import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useSearchParams } from 'react-router-dom'
import dayjs from 'dayjs'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Tooltip from '@mui/material/Tooltip'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import ContainerPage from '../../../components/ContainerPage/ContainerPage'
import {
  loadCandidates,
  approveBatch,
  toggleRow,
  setSelection,
  setOverride,
  setQuickFilter,
  clearLastApproval,
  clearError,
  excludePayments,
  restorePayments,
  loadExcluded,
} from '../../../features/bulkProcessing/bulkProcessing'
import { selectFeatureFlag } from '../../../features/featureFlags/selectors'
import { PAYMENTS_PERSISTENT_SKIP } from '../../../config/featureFlagKeys'
import { showSnackBar, showUndoToast } from '../../../components/Snackbar/Snackbar'
import { useUiKit } from '../../../components/UiKit/UiKitContext'
import { failureOf } from '../../../utils/errorHelpers'
import { businessTimeZone } from '../../../utils/date'
import { downloadCsv } from '../../../utils/csv'
import Calendar from '../../../components/Calendar/Calendar'
import KpiCards from './KpiCards'
import PaymentsTable from './PaymentsTable'
import SkippedPaymentsTable from './SkippedPaymentsTable'
import SkipPaymentsModal from './SkipPaymentsModal'
import SummaryBar from './SummaryBar'
import ConfirmDrawer from './ConfirmDrawer'
import BacklogBanner from './BacklogBanner'
import {
  filterRows,
  filterActiveRows,
  filterFacetRows,
  filterDueRows,
  filterOverdueRows,
  buildOverdueBreakdown,
  getFilterCounts,
  computeSelectionSummary,
  buildProcessItems,
  buildOverridePatch,
  buildProcessorGroups,
  buildProcessorSelection,
  buildDueDateBreakdown,
  SKIPPED_FILTER_KEY,
  DUE_FILTER_ALL,
  DUE_FILTER_OVERDUE,
  formatDate,
} from './helpers'
import DateRangePresets from '../shared/DateRangePresets'
import {
  DAILY_PRESETS,
  DEFAULT_DAILY_PRESET_KEY,
  CUSTOM_PRESET_KEY,
  getPresetRange,
  todayBusinessKey,
} from '../shared/presetRanges'

// The To Process tab defaults to TODAY only. The overdue backlog and the upcoming window are
// their own tabs rather than one merged range: until 2026-07-28 the default spanned
// today-90 .. today+3, and a "select all clean" over it charged 104 payments due 07/29-07/31
// three days early. Separate populations mean an operator always knows which one they're
// acting on. All boundaries are computed in businessTimeZone (see presetRanges), never the
// operator's browser timezone.
const defaultRange = () => getPresetRange(DEFAULT_DAILY_PRESET_KEY)

const NO_ROWS = []

const paymentCount = (count) => `${count} payment${count === 1 ? '' : 's'}`

const inertCell = (value) => {
  const text = String(value ?? '').replace(/[\r\n]+/g, ' ')
  return /^[=+\-@\t]/.test(text) ? `'${text}` : text
}

const refusalMessage = (refused, outcome) => {
  const reasons = [...new Set(refused.map((item) => item.reason).filter(Boolean))]
  return `${paymentCount(refused.length)} not ${outcome}: ${reasons.join(' ')}`
}

const DailyProcessing = ({ embedded = false }) => {
  const dispatch = useDispatch()
  const { confirm: kitConfirm } = useUiKit()
  const navigate = useNavigate()
  const {
    rows,
    kpis,
    loading,
    error,
    selection,
    overrides,
    quickFilter,
    lastApproval,
    excludedRows,
    excludedLoading,
    skipping,
  } = useSelector((state) => state.bulkProcessing)
  const persistentSkipOn = useSelector(selectFeatureFlag(PAYMENTS_PERSISTENT_SKIP))
  const [skipTargets, setSkipTargets] = useState([])

  // The whole view — date range + every facet + (in PaymentsTable) page and sort — is seeded from
  // the URL and mirrored back to it (see the sync effect below). That way opening a client from a
  // row and pressing Back returns the reviewer to the exact range/filters/page they were on,
  // instead of resetting to the default Today view. On a bare URL every value falls back to its
  // default, so a fresh visit still opens on Today only.
  const [searchParams, setSearchParams] = useSearchParams()

  const [{ from, to }, setRange] = useState(() => {
    // Seed each end independently: whichever end equals its default is dropped from the URL, so a
    // wide range like from=Jan-1 with to=today arrives back as ?from= only. Requiring BOTH would
    // discard the present one and snap back to Today.
    const def = defaultRange()
    const f = searchParams.get('from')
    const t = searchParams.get('to')
    return { from: f ? dayjs(f) : def.from, to: t ? dayjs(t) : def.to }
  })
  // Which quick date-range chip is active. The default range is overdue-inclusive, so the
  // chip starts on the overdue+today preset; any manual calendar edit flips it to 'custom'.
  const [datePreset, setDatePreset] = useState(() => searchParams.get('preset') || DEFAULT_DAILY_PRESET_KEY)
  // Client-side due-date-dimension filter over the loaded (overdue-inclusive) rows:
  // 'all' | 'overdue' | a specific 'YYYY-MM-DD' business day (backlog banner click). Never
  // a refetch — the default range already loads everything on-or-before today.
  const [dueFilter, setDueFilter] = useState(() => searchParams.get('due') || DUE_FILTER_ALL)
  // Facet filters layered under the quick-filter chips — payment method type and
  // billing-plan status. Client-side only; counts and the table follow them.
  const [paymentType, setPaymentType] = useState(() => searchParams.get('type') || 'all')
  // Plan-status facet defaults to active-only: the overdue-inclusive range surfaces a large
  // stranded-inactive-plan backlog that must never be auto-processed, so it's noise here by
  // default. Operators reveal inactive (or null-plan) rows deliberately via the Plan control.
  const [planFilter, setPlanFilter] = useState(() => searchParams.get('plan') || 'active')
  const [initialLoadDone, setInitialLoadDone] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  // Explicit opt-in to charge not-yet-due payments early. Deliberately NOT sticky: it resets
  // every time the drawer opens, so an earlier "yes, charge the weekend ahead" can never carry
  // silently into the next batch.
  const [allowFutureDue, setAllowFutureDue] = useState(false)
  const [showSkipped, setShowSkipped] = useState(() => searchParams.get('skipped') === '1')

  // Mirror the view state into the URL so it rides in the browser history entry — the other half
  // of the seed-from-URL above. replace:true keeps it a mirror rather than a navigation, and each
  // key is dropped when it equals its default so the URL stays clean on the default Today view.
  // Guarded by a string compare (and reading searchParams as a dep) so it never loops: once the
  // URL matches the state, it stops writing, including on PaymentsTable's own ?page=/?sort= writes.
  useEffect(() => {
    const def = defaultRange()
    const next = new URLSearchParams(searchParams)
    const put = (key, value, fallback) => {
      if (value === undefined || value === null || value === '' || value === fallback) next.delete(key)
      else next.set(key, value)
    }
    put('from', from.format('YYYY-MM-DD'), def.from.format('YYYY-MM-DD'))
    put('to', to.format('YYYY-MM-DD'), def.to.format('YYYY-MM-DD'))
    put('preset', datePreset, DEFAULT_DAILY_PRESET_KEY)
    put('due', dueFilter, DUE_FILTER_ALL)
    put('type', paymentType, 'all')
    put('plan', planFilter, 'active')
    put('skipped', showSkipped ? '1' : '0', '0')
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true })
    }
  }, [from, to, datePreset, dueFilter, paymentType, planFilter, showSkipped, searchParams, setSearchParams])

  const fetchCandidates = (range = { from, to }, options = {}) =>
    dispatch(
      loadCandidates({
        from: range.from.format('YYYY-MM-DD'),
        to: range.to.format('YYYY-MM-DD'),
        ...options,
      })
    )

  useEffect(() => {
    let active = true
    fetchCandidates().finally(() => {
      if (active) setInitialLoadDone(true)
    })
    return () => {
      active = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const reloadExcluded = () =>
    dispatch(loadExcluded({ from: from.format('YYYY-MM-DD'), to: to.format('YYYY-MM-DD') }))

  const rangeRef = useRef({ from, to })
  rangeRef.current = { from, to }

  useEffect(() => {
    if (!persistentSkipOn || !showSkipped) return
    dispatch(loadExcluded({ from: from.format('YYYY-MM-DD'), to: to.format('YYYY-MM-DD') }))
  }, [persistentSkipOn, showSkipped, from, to, dispatch])

  // A From/To change must re-query — the range is a server-side filter, not a
  // client-side one, so without a refetch the table keeps showing the old range.
  // A manual calendar edit also demotes the active quick-range chip to 'custom'.
  const handleChangeFrom = (value) => {
    const next = { from: value, to }
    setDatePreset(CUSTOM_PRESET_KEY)
    setDueFilter(DUE_FILTER_ALL)
    setRange(next)
    fetchCandidates(next)
  }
  const handleChangeTo = (value) => {
    const next = { from, to: value }
    setDatePreset(CUSTOM_PRESET_KEY)
    setDueFilter(DUE_FILTER_ALL)
    setRange(next)
    fetchCandidates(next)
  }

  const handleSelectPreset = (key) => {
    setDatePreset(key)
    // A new server range invalidates the client-side due filter — it was scoped to the
    // previous range's rows.
    setDueFilter(DUE_FILTER_ALL)
    // 'custom' imposes no range — it just hands control back to the calendars.
    const preset = getPresetRange(key)
    if (!preset) return
    setRange(preset)
    fetchCandidates(preset)
  }

  // The "Overdue (n)" chip toggles between showing only past-due rows and the whole loaded
  // range. It's a client-side slice, not a refetch — the default range already carries the
  // overdue backlog.
  const handleToggleOverdue = () =>
    setDueFilter((prev) => (prev === DUE_FILTER_OVERDUE ? DUE_FILTER_ALL : DUE_FILTER_OVERDUE))
  const handleSelectBacklogDay = (dateKey) => setDueFilter(dateKey)
  const handleShowAllDates = () => setDueFilter(DUE_FILTER_ALL)

  // Show-then-consume: these notices live in redux (which survives a tab switch /
  // remount), so they must be cleared once shown or they replay on every remount.
  useEffect(() => {
    if (!error) return
    showSnackBar('error', error)
    dispatch(clearError())
  }, [error, dispatch])

  useEffect(() => {
    if (!lastApproval) return
    const count = lastApproval.approved ?? 0
    // WS 2146091: the server returns the payments it refused to approve, with the reason. Shown,
    // rather than a smaller count with nothing said.
    const refused = lastApproval.refused || []
    if (count > 0 || refused.length === 0) {
      showSnackBar('success', `Approved ${count} payment${count === 1 ? '' : 's'} — they'll be charged within ~15 min.`)
    }
    if (refused.length > 0) {
      const reasons = [...new Set(refused.map((item) => item.reason).filter(Boolean))]
      showSnackBar('error', `${refused.length} payment${refused.length === 1 ? '' : 's'} not approved: ${reasons.join(' ')}`)
    }
    dispatch(clearLastApproval())
  }, [lastApproval, dispatch])

  // Today's business day, the reference point for overdue bucketing — resolved once per
  // mount (a session crossing midnight is negligible for this) in businessTimeZone.
  const todayKey = useMemo(() => todayBusinessKey(), [])

  // Facet filters (type + plan status) apply UNDER the quick-filter chips: the
  // chips, their counts and the table all read the facet-filtered set, so a chip
  // count always matches what clicking it would show.
  const facetRows = useMemo(() => filterFacetRows(rows, { paymentType, planFilter }), [rows, paymentType, planFilter])
  // Overdue backlog derived from the facet-filtered set (so it honors the type/plan facets
  // but is independent of the due filter itself — clicking "Overdue" must not shrink its own
  // badge). Drives the Overdue chip count and the backlog banner's per-day breakdown.
  const overdueRows = useMemo(() => filterOverdueRows(facetRows, todayKey), [facetRows, todayKey])
  const overdueCount = overdueRows.length
  const overdueBreakdown = useMemo(() => buildOverdueBreakdown(facetRows, todayKey), [facetRows, todayKey])
  // Due-date-dimension slice (all / overdue / a specific day), layered between the facets
  // and the status quick-filter so the status chip counts and the table stay in agreement.
  const dueRows = useMemo(() => filterDueRows(facetRows, dueFilter, todayKey), [facetRows, dueFilter, todayKey])
  const activeQuickFilter = persistentSkipOn && quickFilter === SKIPPED_FILTER_KEY ? 'all' : quickFilter
  const quickFilteredRows = useMemo(
    () => filterRows(dueRows, activeQuickFilter, overrides),
    [dueRows, activeQuickFilter, overrides]
  )
  // Skipped rows are hidden from every quick filter by default. Exception: the
  // existing "Skipped" quick-filter chip already means "show me the skipped
  // rows", so don't let the hide-layer immediately filter its own results back
  // out — treat that as an implicit showSkipped.
  const effectiveShowSkipped = showSkipped || activeQuickFilter === SKIPPED_FILTER_KEY
  const filteredRows = useMemo(
    () => filterActiveRows(quickFilteredRows, overrides, { showSkipped: effectiveShowSkipped }),
    [quickFilteredRows, overrides, effectiveShowSkipped]
  )
  const skipSelectedRows = useMemo(
    () => (persistentSkipOn ? filteredRows.filter((row) => !!selection[row.paymentId]) : NO_ROWS),
    [persistentSkipOn, filteredRows, selection]
  )
  // A stable signature of everything that defines WHICH payments are shown — the server range
  // plus the client-side facets. PaymentsTable resets to page 1 when this changes (the operator
  // deliberately changed the query), but NOT when a row is merely approved or skipped out of the
  // existing set, so a reviewer working down a long list keeps their place. quickFilter is passed
  // separately and folded into the reset key there, so it is intentionally not repeated here.
  const viewSignature = useMemo(
    () =>
      [from.format('YYYY-MM-DD'), to.format('YYYY-MM-DD'), dueFilter, paymentType, planFilter, effectiveShowSkipped].join(
        '|'
      ),
    [from, to, dueFilter, paymentType, planFilter, effectiveShowSkipped]
  )
  const counts = useMemo(() => getFilterCounts(dueRows, overrides), [dueRows, overrides])
  const skippedCount = counts.skipped ?? 0
  const selectionSummary = useMemo(
    () => computeSelectionSummary(rows, selection, overrides, { todayKey, allowFutureDue }),
    [rows, selection, overrides, todayKey, allowFutureDue]
  )
  // Which due dates the pending batch covers — rendered in the confirm drawer so the operator
  // always confirms the dates they're charging, not just a count.
  const selectionDueDates = useMemo(
    () => buildDueDateBreakdown(rows, selection, overrides, todayKey),
    [rows, selection, overrides, todayKey]
  )
  const cleanCount = useMemo(() => rows.filter((row) => row.status === 'Ready').length, [rows])

  const allVisibleSelected = filteredRows.length > 0 && filteredRows.every((row) => !!selection[row.paymentId])
  const someVisibleSelected = filteredRows.some((row) => !!selection[row.paymentId])

  // Read `selection` through a ref so the callback identity stays stable. Depending on
  // `selection` directly would change this handler on every click, which re-renders every
  // PaymentRow and defeats the memo — the exact cost this is meant to remove. We only need
  // the value at click time, never during render.
  const selectionRef = useRef(selection)
  selectionRef.current = selection

  const handleToggleRow = useCallback((row) => {
    const wasChecked = !!selectionRef.current[row.paymentId]
    dispatch(toggleRow(row.paymentId))
    if (row.status === 'Needs Review') {
      dispatch(setOverride({ paymentId: row.paymentId, patch: { allowBounced: !wasChecked } }))
    }
  }, [dispatch])

  const handleToggleAllVisible = (checked) => {
    const next = { ...selection }
    filteredRows.forEach((row) => {
      next[row.paymentId] = checked
      // Mirror the per-row checkbox: a Needs-Review (bounced) row must carry the
      // allowBounced flag matching its selected state, or it gets sent to
      // bulk-approve with allowBounced:false and is silently skipped server-side.
      if (row.status === 'Needs Review') {
        dispatch(setOverride({ paymentId: row.paymentId, patch: { allowBounced: checked } }))
      }
    })
    dispatch(setSelection(next))
  }

  const handleSelectAllClean = () => {
    // Scope to the VISIBLE (filtered) rows, mirroring the header checkbox: with a
    // type/plan/date/status filter applied, "select all clean" must select the ready rows
    // the operator can actually see, not ready rows hidden behind the filter. Off-filter
    // selections are preserved (spread), so clean-selecting under one filter then another is additive.
    const next = { ...selection }
    filteredRows.forEach((row) => {
      next[row.paymentId] = row.status === 'Ready'
    })
    dispatch(setSelection(next))
  }

  // Processor groups are derived from the SAME visible/filtered rows the table renders (facet
  // + date + quick filters already applied), so a group's count matches what selecting it does.
  const processorGroups = useMemo(() => buildProcessorGroups(filteredRows), [filteredRows])

  // Select one processor's Ready rows and, since setSelection replaces the whole map, deselect
  // everything else. Grouping and selection MUST read the same filteredRows collection.
  const handleSelectByProcessor = useCallback(
    (processorKey) => dispatch(setSelection(buildProcessorSelection(filteredRows, processorKey))),
    [dispatch, filteredRows]
  )

  const handleOverrideChange = useCallback((row, value) => {
    if (persistentSkipOn && value === 'skip') {
      setSkipTargets([row])
      return
    }
    dispatch(setOverride({ paymentId: row.paymentId, patch: buildOverridePatch(value) }))
  }, [dispatch, persistentSkipOn])

  const handleSkipSelected = () => setSkipTargets(skipSelectedRows)

  const handleConfirmSkip = async (reason) => {
    const paymentIds = skipTargets.map((row) => row.paymentId)
    setSkipTargets([])
    const result = await dispatch(excludePayments({ paymentIds, reason }))
    if (!excludePayments.fulfilled.match(result)) return
    const { excluded: skippedTotal = 0, refused = [] } = result.payload?.data || {}
    if (skippedTotal > 0) showSnackBar('success', `Skipped ${paymentCount(skippedTotal)}.`)
    if (refused.length > 0) showSnackBar('error', refusalMessage(refused, 'skipped'))
    fetchCandidates({ from, to }, { keepSelection: true })
  }

  const handleConfirmSkipOrThrow = async (reason) => {
    const paymentIds = skipTargets.map((row) => row.paymentId)
    const result = await dispatch(excludePayments({ paymentIds, reason }))
    if (!excludePayments.fulfilled.match(result)) throw failureOf(result, 'The payments were not skipped.')
    const { excluded: skippedTotal = 0, refused = [] } = result.payload?.data || {}
    const refusedIds = new Set(refused.map((item) => item.paymentId))
    const skippedIds = paymentIds.filter((paymentId) => !refusedIds.has(paymentId))
    if (skippedTotal > 0) showUndoToast(`Skipped ${paymentCount(skippedTotal)}.`, () => undoSkip(skippedIds))
    if (refused.length > 0) showSnackBar('error', refusalMessage(refused, 'skipped'))
    fetchCandidates(rangeRef.current, { keepSelection: true })
  }

  const undoSkip = async (paymentIds) => {
    const result = await dispatch(restorePayments({ paymentIds }))
    if (!restorePayments.fulfilled.match(result)) return
    const { restored = 0, refused = [] } = result.payload?.data || {}
    if (restored > 0) showSnackBar('success', `Restored ${paymentCount(restored)}.`)
    if (refused.length > 0) showSnackBar('error', refusalMessage(refused, 'restored'))
    const range = rangeRef.current
    fetchCandidates(range, { keepSelection: true })
    dispatch(loadExcluded({ from: range.from.format('YYYY-MM-DD'), to: range.to.format('YYYY-MM-DD') }))
  }

  const handleRestore = async (paymentIds) => {
    const result = await dispatch(restorePayments({ paymentIds }))
    if (!restorePayments.fulfilled.match(result)) return
    const { restored = 0, refused = [] } = result.payload?.data || {}
    if (restored > 0) showSnackBar('success', `Restored ${paymentCount(restored)}.`)
    if (refused.length > 0) showSnackBar('error', refusalMessage(refused, 'restored'))
    fetchCandidates({ from, to }, { keepSelection: true })
    reloadExcluded()
  }

  const handleViewClient = useCallback((row) => {
    if (row?.userId) navigate(`/contact/${row.userId}`)
    else showSnackBar('alert', 'No client record linked to this payment.')
  }, [navigate])

  const handleOpenNotes = useCallback((row) => {
    if (row?.userId) navigate(`/contact/${row.userId}?activeTab=notes`)
    else showSnackBar('alert', 'No client record linked to this payment.')
  }, [navigate])

  const skippedViewOpen = persistentSkipOn && showSkipped

  const handleExportSkipped = () => {
    if (!excludedRows.length) {
      showSnackBar('alert', 'Nothing to export.')
      return
    }
    downloadCsv(
      `skipped-payments-${dayjs().tz(businessTimeZone).format('YYYY-MM-DD')}.csv`,
      [
        { label: 'Client', value: (r) => inertCell(r.clientName) },
        { label: 'Client Code', value: (r) => r.clientCode },
        { label: 'Amount', value: (r) => Number(r.amount || 0).toFixed(2) },
        { label: 'Payment', value: (r) => `#${r.pmt}` },
        { label: 'Due Date', value: (r) => formatDate(r.dueDate) },
        { label: 'Skipped By', value: (r) => inertCell(r.exclusion?.byName) },
        { label: 'Skipped On', value: (r) => formatDate(r.exclusion?.at) },
        { label: 'Reason', value: (r) => inertCell(r.exclusion?.reason) },
      ],
      excludedRows
    )
  }

  const handleExport = () => {
    if (skippedViewOpen) {
      handleExportSkipped()
      return
    }
    if (!filteredRows.length) {
      showSnackBar('alert', 'Nothing to export.')
      return
    }
    downloadCsv(
      `daily-payment-processing-${dayjs().tz(businessTimeZone).format('YYYY-MM-DD')}.csv`,
      [
        { label: 'Client', value: (r) => r.clientName },
        { label: 'Client Code', value: (r) => r.clientCode },
        { label: 'Amount', value: (r) => Number(r.amount || 0).toFixed(2) },
        { label: 'Payment', value: (r) => `#${r.pmt}${r.totalPayments ? ` of ${r.totalPayments}` : ''}` },
        { label: 'Method', value: (r) => r.billingMethod?.type || '' },
        { label: 'Recommended Gateway', value: (r) => r.recommendedGateway || '' },
        { label: 'Status', value: (r) => r.status },
        { label: 'Attempts', value: (r) => r.attempts ?? 0 },
      ],
      filteredRows
    )
  }

  const handleProcessorHistory = () => {
    navigate('/payments/processed-payments')
  }

  const handleProcess = () => {
    // Guard on the SELECTION, not on summary.count: with future-dated rows selected and the
    // opt-in still off, count is 0 while there is plainly something to confirm — refusing to
    // open the drawer there would leave the operator with a dead "Process" button and no
    // explanation of why their selection isn't chargeable.
    if (!Object.values(selection).some(Boolean)) return
    setAllowFutureDue(false)
    setDrawerOpen(true)
  }

  const handleConfirm = async () => {
    const items = buildProcessItems(rows, selection, overrides, { todayKey, allowFutureDue })
    setSubmitting(true)
    const result = await dispatch(approveBatch({ items }))
    setSubmitting(false)
    if (approveBatch.fulfilled.match(result)) {
      setDrawerOpen(false)
      // Reload the queue (using the currently selected range) so approved rows
      // reflect their new state (and drop out once the worker charges them).
      fetchCandidates()
    }
  }

  const showInitialLoading = loading && !initialLoadDone
  const skippedChipCount = persistentSkipOn ? kpis?.excludedCount ?? 0 : skippedCount
  let skippedChipTitle = effectiveShowSkipped ? 'Hide skipped payments' : 'Show payments skipped from processing'
  if (persistentSkipOn) skippedChipTitle = showSkipped ? 'Back to the queue' : 'Show skipped payments'

  const body = (
      <div className="flex flex-col gap-6" data-testid="daily-processing-screen">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          {!embedded ? (
            <div>
              <h1 className="text-xl font-semibold text-text-primary">Daily Payment Processing</h1>
              <p className="text-sm text-text-secondary mt-1">Review and approve client payments due today, plus any overdue backlog.</p>
            </div>
          ) : (
            <p className="text-sm text-text-secondary">Review and approve client payments due today, plus any overdue backlog.</p>
          )}
          <div className="flex gap-2">
            <Button variant="outlined" onClick={handleExport}>
              Export
            </Button>
            {!embedded && (
              <Button variant="outlined" onClick={handleProcessorHistory}>
                Processor History
              </Button>
            )}
          </div>
        </div>

        <div
          className="rounded-2xl border border-divider bg-paper p-4 flex flex-wrap items-end gap-3"
          data-testid="daily-processing-date-range"
        >
          <div className="w-full flex flex-wrap items-center gap-x-3 gap-y-2">
            <DateRangePresets presets={DAILY_PRESETS} active={datePreset} onSelect={handleSelectPreset} />
            {/* Overdue quick-filter: a live count of past-due carryover in the loaded range,
                one click to show just those rows. Hidden when there's no backlog — nothing
                to filter to. */}
            {overdueCount > 0 && (
              <Tooltip title="Show only payments due earlier that haven't been processed">
                <Chip
                  size="small"
                  data-testid="overdue-filter-chip"
                  onClick={handleToggleOverdue}
                  aria-pressed={dueFilter === DUE_FILTER_OVERDUE}
                  color={dueFilter === DUE_FILTER_OVERDUE ? 'warning' : 'default'}
                  variant={dueFilter === DUE_FILTER_OVERDUE ? 'filled' : 'outlined'}
                  label={`Overdue ${overdueCount}`}
                />
              </Tooltip>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">From</label>
            <Calendar value={from} onChange={handleChangeFrom} withLabel={false} />
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">To</label>
            <Calendar value={to} onChange={handleChangeTo} withLabel={false} />
          </div>
          <div className="flex flex-col">
            <label className="block text-xs font-medium text-text-secondary mb-1">Type</label>
            <Select
              size="small"
              value={paymentType}
              onChange={(e) => setPaymentType(e.target.value)}
              data-testid="daily-type-filter"
              sx={{ minWidth: 110 }}
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="ACH">ACH</MenuItem>
              <MenuItem value="Card">Card</MenuItem>
            </Select>
          </div>
          <div className="flex flex-col">
            <label className="block text-xs font-medium text-text-secondary mb-1">Plan</label>
            <Select
              size="small"
              value={planFilter}
              onChange={(e) => setPlanFilter(e.target.value)}
              data-testid="daily-plan-filter"
              sx={{ minWidth: 130 }}
            >
              <MenuItem value="all">All Plans</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </div>
        </div>

        <BacklogBanner
          total={overdueCount}
          breakdown={overdueBreakdown}
          activeDay={dueFilter}
          onSelectDay={handleSelectBacklogDay}
          onShowAll={handleShowAllDates}
        />

        <KpiCards kpis={kpis} />

        {/* While the pre-existing "Skipped" quick-filter is active it already governs
            skipped-row visibility (effectiveShowSkipped is forced true), which makes this
            toggle a functional no-op in that state. Rendering it there previously let an
            operator click a toggle that visibly looked active/clickable but did nothing —
            hide it instead; the quick-filter chip alone covers that case. */}
        {activeQuickFilter !== SKIPPED_FILTER_KEY && (
          <div className="flex items-center justify-end">
            <Tooltip title={skippedChipTitle}>
              <Chip
                size="small"
                data-testid="skipped-toggle-chip"
                onClick={() => setShowSkipped((prev) => !prev)}
                aria-pressed={effectiveShowSkipped}
                color={effectiveShowSkipped ? 'primary' : 'default'}
                variant={effectiveShowSkipped ? 'filled' : 'outlined'}
                label={`Skipped (${skippedChipCount})`}
              />
            </Tooltip>
          </div>
        )}

        {skippedViewOpen ? (
          <SkippedPaymentsTable
            rows={excludedRows}
            loading={excludedLoading}
            restoring={skipping}
            onRestore={handleRestore}
          />
        ) : (
          <PaymentsTable
            rows={filteredRows}
            selection={selection}
            overrides={overrides}
            counts={counts}
            quickFilter={activeQuickFilter}
            viewSignature={viewSignature}
            allVisibleSelected={allVisibleSelected}
            someVisibleSelected={someVisibleSelected}
            cleanCount={cleanCount}
            processorGroups={processorGroups}
            loading={showInitialLoading}
            totalRows={rows.length}
            onChangeFilter={(key) => dispatch(setQuickFilter(key))}
            onToggleRow={handleToggleRow}
            onToggleAllVisible={handleToggleAllVisible}
            onSelectAllClean={handleSelectAllClean}
            onSelectProcessor={handleSelectByProcessor}
            onOverrideChange={handleOverrideChange}
            onViewClient={handleViewClient}
            onOpenNotes={handleOpenNotes}
            hideSkippedFilter={persistentSkipOn}
            onSkipSelected={persistentSkipOn ? handleSkipSelected : undefined}
            skipSelectedCount={skipSelectedRows.length}
          />
        )}

        {/* Spacer so the last table rows clear the fixed bottom bar. */}
        <div aria-hidden className="h-20" />

        {!skippedViewOpen && (
          <SummaryBar summary={selectionSummary} totalRows={rows.length} onProcess={handleProcess} />
        )}

        <ConfirmDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          summary={selectionSummary}
          submitting={submitting}
          onConfirm={handleConfirm}
          dueDates={selectionDueDates}
          allowFutureDue={allowFutureDue}
          onToggleAllowFutureDue={setAllowFutureDue}
          showSkippedRow={!persistentSkipOn}
        />

        {persistentSkipOn && (
          <SkipPaymentsModal
            open={skipTargets.length > 0}
            rows={skipTargets}
            onCancel={() => setSkipTargets([])}
            onConfirm={kitConfirm ? handleConfirmSkipOrThrow : handleConfirmSkip}
          />
        )}
      </div>
  )

  return embedded ? body : <ContainerPage>{body}</ContainerPage>
}

export default DailyProcessing
